/**
 * BehaviorConfigTab Component
 * Configure agent behavior: rules, transfer conditions, max attempts,
 * notification channels, WhatsApp config, department, and working hours.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import {
  IconRuler,
  IconAlertTriangle,
  IconPlus,
  IconX,
  IconCheck,
  IconBell,
  IconBrandWhatsapp,
  IconBuilding,
  IconCalendar,
  IconUserPlus,
  IconClock,
  IconInfoCircle,
  IconPhoto,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import { TRANSFER_CONDITIONS, TIME_OPTIONS } from '../../constants';
import { api } from '../../../../api/Middleware';
import AgentsFlowService from '../../services/AgentsFlowService';
import NumberFormat from 'react-number-format';
import './BehaviorConfigTab.css';

// ===============================================
// CONSTANTS
// ===============================================

const RULE_SUGGESTIONS = [
  'Sempre seja educado e respeitoso',
  'Não forneça informações médicas específicas',
  'Confirme os dados antes de agendar',
  'Não prometa descontos sem autorização',
  'Encaminhe reclamações para um humano',
];

const TRANSFER_SUGGESTIONS = [
  'Antes de realizar qualquer agendamento',
  'Quando o cliente solicitar informações sobre preços',
  'Após coletar os dados do cliente',
  'Quando o assunto envolver questões financeiras',
  'Se o cliente mencionar problemas técnicos',
];

const DEFAULT_WHATSAPP_TEMPLATE =
  '\u{1F916}\u27A1\uFE0F\u{1F464} Intervenção Solicitada\n' +
  '\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\n' +
  'A IA identificou que este cliente precisa de atendimento humano.\n' +
  '\u{1F4F1} Contato: {client_phone}\n' +
  '\u{1F517} wa.me/{client_phone_clean}\n' +
  '\u{1F550} {datetime}\n' +
  '\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\n' +
  '\u{1F4DD} Detalhes:\n' +
  '\u{1F4CB} Resumo: {context_summary}\n' +
  'Motivo: {reason}';

const WEEKDAYS = [
  { value: 'seg', label: 'Seg' },
  { value: 'ter', label: 'Ter' },
  { value: 'qua', label: 'Qua' },
  { value: 'qui', label: 'Qui' },
  { value: 'sex', label: 'Sex' },
  { value: 'sab', label: 'Sáb' },
  { value: 'dom', label: 'Dom' },
];

// Agenda de desligamento da IA: uma janela OFF por dia (default: nenhum dia
// desligado). Fora das janelas a IA responde normalmente.
const buildDefaultScheduleWindows = () =>
  WEEKDAYS.reduce((acc, d) => {
    acc[d.value] = { off: false, start: '08:00', end: '18:00' };
    return acc;
  }, {});

const buildDefaultAgentSchedule = () => ({
  enabled: false,
  windows: buildDefaultScheduleWindows(),
});

// ===============================================
// DEPARTMENT SELECT (loads from API)
// ===============================================

const DepartmentSelect = ({ value, onChange }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadDepartments = async () => {
      setLoading(true);
      try {
        const response = await api.get('/user-departments/');
        const data = response.data?.results || response.data || [];
        setDepartments(data.filter(item => item.is_active !== false));
      } catch (err) {
        console.error('Error loading departments:', err);
      }
      setLoading(false);
    };
    loadDepartments();
  }, []);

  return (
    <select
      className="bh-select"
      value={value || ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
      disabled={loading}
    >
      <option value="">{loading ? 'Carregando...' : 'Selecione um departamento...'}</option>
      {departments.map((dept) => (
        <option key={dept.id} value={dept.id}>
          {dept.name}
        </option>
      ))}
    </select>
  );
};

// ===============================================
// PHONE LIST (multiple phone inputs)
// ===============================================

const PhoneList = ({ phones, onChange, max = 3 }) => {
  const list = Array.isArray(phones) && phones.length > 0 ? phones : [''];

  const handlePhoneChange = (index, rawValue) => {
    const updated = [...list];
    updated[index] = rawValue;
    onChange(updated.filter(p => p !== ''));
  };

  const addPhone = () => {
    if (list.length < max) {
      onChange([...list, '']);
    }
  };

  const removePhone = (index) => {
    const updated = list.filter((_, i) => i !== index);
    onChange(updated.length > 0 ? updated : ['']);
  };

  return (
    <div>
      <span className="bh-whatsapp__label">Números para notificação (máx. {max})</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {list.map((phone, index) => (
          <div key={index} className="bh-phone-row">
            <NumberFormat
              format={phone.length >= 3 && phone[2] !== '9' ? '(##) ####-####' : '(##) #####-####'}
              mask="_"
              className="bh-input"
              placeholder="(11) 99999-9999"
              value={phone}
              onValueChange={(values) => handlePhoneChange(index, values.value)}
            />
            {list.length > 1 && (
              <button
                type="button"
                className="bh-phone-row__remove"
                onClick={() => removePhone(index)}
              >
                &times;
              </button>
            )}
          </div>
        ))}
      </div>
      {list.length < max && (
        <button type="button" className="bh-add-btn" onClick={addPhone} style={{ marginTop: '0.5rem' }}>
          <IconPlus size={12} />
          Adicionar telefone
        </button>
      )}
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const BehaviorConfigTab = ({ agentId }) => {
  const { state, updateBehaviorConfig } = useAgentsFlow();
  const { editor } = state;

  const [config, setConfig] = useState({
    rules: [],
    transfer_conditions: ['client_request', 'complaint'],
    max_attempts_before_transfer: 3,
    custom_transfer_rules: [],
    department_id: null,
    notification_channels: ['plataforma'],
    whatsapp_phones: [],
    whatsapp_template: DEFAULT_WHATSAPP_TEMPLATE,
    enforce_working_hours: false,
    working_hours_start: '08:00',
    working_hours_end: '18:00',
    working_days: ['seg', 'ter', 'qua', 'qui', 'sex'],
    out_of_hours_action: 'continue_ai',
    out_of_hours_message: 'No momento não há atendentes disponíveis. Nosso horário de atendimento humano é de {hours_start} às {hours_end}, {working_days}. Posso continuar te ajudando!',
    // Limite de imagens vindas da base de conhecimento (search_knowledge_base).
    // 0 = sem limite (mantém comportamento de agentes existentes).
    max_images_per_response: 0,
    // Agenda de desligamento da IA por horário (gate global por agente).
    agent_schedule: buildDefaultAgentSchedule(),
  });

  const [newRule, setNewRule] = useState('');
  const [newTransferRule, setNewTransferRule] = useState('');

  // Reactivation config (per-agent)
  const [reactivationConfig, setReactivationConfig] = useState({
    enabled: false,
    auto_reactivation_minutes: 10,
  });
  const [reactivationLoading, setReactivationLoading] = useState(false);

  // Load existing config
  useEffect(() => {
    if (editor?.behavior_config) {
      setConfig(prev => ({
        ...prev,
        rules: editor.behavior_config.rules || [],
        transfer_conditions: editor.behavior_config.transfer_conditions || ['client_request', 'complaint'],
        max_attempts_before_transfer: editor.behavior_config.max_attempts_before_transfer || 3,
        custom_transfer_rules: editor.behavior_config.custom_transfer_rules || [],
        department_id: editor.behavior_config.department_id || null,
        notification_channels: editor.behavior_config.notification_channels || ['plataforma'],
        whatsapp_phones: editor.behavior_config.whatsapp_phones || [],
        whatsapp_template: editor.behavior_config.whatsapp_template || DEFAULT_WHATSAPP_TEMPLATE,
        enforce_working_hours: editor.behavior_config.enforce_working_hours || false,
        working_hours_start: editor.behavior_config.working_hours_start || '08:00',
        working_hours_end: editor.behavior_config.working_hours_end || '18:00',
        working_days: editor.behavior_config.working_days || ['seg', 'ter', 'qua', 'qui', 'sex'],
        out_of_hours_action: editor.behavior_config.out_of_hours_action || 'continue_ai',
        out_of_hours_message: editor.behavior_config.out_of_hours_message || 'No momento não há atendentes disponíveis. Nosso horário de atendimento humano é de {hours_start} às {hours_end}, {working_days}. Posso continuar te ajudando!',
        max_images_per_response: editor.behavior_config.max_images_per_response ?? 0,
        agent_schedule: editor.behavior_config.agent_schedule
          ? {
              enabled: editor.behavior_config.agent_schedule.enabled || false,
              // Mescla com defaults para garantir os 7 dias presentes.
              windows: { ...buildDefaultScheduleWindows(), ...(editor.behavior_config.agent_schedule.windows || {}) },
            }
          : buildDefaultAgentSchedule(),
      }));
    }
  }, [editor?.behavior_config]);

  // Load reactivation config
  useEffect(() => {
    if (!agentId) return;
    const loadReactivation = async () => {
      try {
        const res = await AgentsFlowService.getReactivationConfig(agentId);
        if (res.data) {
          setReactivationConfig({
            enabled: res.data.enabled ?? false,
            auto_reactivation_minutes: res.data.auto_reactivation_minutes ?? 10,
          });
        }
      } catch (err) {
        console.error('Error loading reactivation config:', err);
      }
    };
    loadReactivation();
  }, [agentId]);

  const saveReactivationConfig = useCallback(async (newConfig) => {
    setReactivationConfig(newConfig);
    if (!agentId) return;
    setReactivationLoading(true);
    try {
      await AgentsFlowService.updateReactivationConfig(agentId, newConfig);
    } catch (err) {
      console.error('Error saving reactivation config:', err);
    }
    setReactivationLoading(false);
  }, [agentId]);

  const handleChange = useCallback((key, value) => {
    setConfig(prev => {
      const newConfig = { ...prev, [key]: value };
      // Defer context update to after render to avoid setState-during-render
      queueMicrotask(() => updateBehaviorConfig(newConfig));
      return newConfig;
    });
  }, [updateBehaviorConfig]);

  // Rules
  const addRule = useCallback(() => {
    if (newRule.trim()) {
      handleChange('rules', [...config.rules, newRule.trim()]);
      setNewRule('');
    }
  }, [newRule, config.rules, handleChange]);

  const removeRule = useCallback((index) => {
    handleChange('rules', config.rules.filter((_, i) => i !== index));
  }, [config.rules, handleChange]);

  // Transfer conditions
  const toggleCondition = useCallback((conditionId) => {
    const current = config.transfer_conditions;
    const updated = current.includes(conditionId)
      ? current.filter(c => c !== conditionId)
      : [...current, conditionId];
    handleChange('transfer_conditions', updated);
  }, [config.transfer_conditions, handleChange]);

  // Custom transfer rules
  const addTransferRule = useCallback(() => {
    if (newTransferRule.trim() && !(config.custom_transfer_rules || []).includes(newTransferRule.trim())) {
      handleChange('custom_transfer_rules', [...(config.custom_transfer_rules || []), newTransferRule.trim()]);
      setNewTransferRule('');
    }
  }, [newTransferRule, config.custom_transfer_rules, handleChange]);

  const removeTransferRule = useCallback((index) => {
    handleChange('custom_transfer_rules', (config.custom_transfer_rules || []).filter((_, i) => i !== index));
  }, [config.custom_transfer_rules, handleChange]);

  // Notification channels
  const toggleChannel = useCallback((channelId) => {
    const current = config.notification_channels || [];
    const updated = current.includes(channelId)
      ? current.filter(c => c !== channelId)
      : [...current, channelId];
    handleChange('notification_channels', updated);
  }, [config.notification_channels, handleChange]);

  // Working days
  const toggleDay = useCallback((dayValue) => {
    const current = config.working_days || [];
    const updated = current.includes(dayValue)
      ? current.filter(d => d !== dayValue)
      : [...current, dayValue];
    handleChange('working_days', updated);
  }, [config.working_days, handleChange]);

  // Agenda de desligamento da IA
  const toggleScheduleEnabled = useCallback((checked) => {
    const cur = config.agent_schedule || buildDefaultAgentSchedule();
    handleChange('agent_schedule', { ...cur, enabled: checked });
  }, [config.agent_schedule, handleChange]);

  const updateScheduleDay = useCallback((dayValue, patch) => {
    const cur = config.agent_schedule || buildDefaultAgentSchedule();
    const curWindow = cur.windows?.[dayValue] || { off: false, start: '08:00', end: '18:00' };
    handleChange('agent_schedule', {
      ...cur,
      windows: { ...cur.windows, [dayValue]: { ...curWindow, ...patch } },
    });
  }, [config.agent_schedule, handleChange]);

  const whatsappEnabled = (config.notification_channels || []).includes('whatsapp');

  return (
    <div className="bh-config">

      {/* ====== Section 1: Regras de Comportamento ====== */}
      <section className="bh-section">
        <h3 className="bh-section__title">
          <IconRuler size={20} />
          Regras de Comportamento
        </h3>
        <p className="bh-section__subtitle">
          Defina regras que o agente deve seguir durante as conversas. Estas regras ajudam a
          manter o comportamento consistente e alinhado com os valores do seu negócio.
        </p>

        <div className="bh-rule-input">
          <input
            type="text"
            className="bh-rule-input__field"
            placeholder="Digite uma regra de comportamento..."
            value={newRule}
            onChange={(e) => setNewRule(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRule(); } }}
          />
          <button
            type="button"
            className="bh-rule-input__btn"
            onClick={addRule}
            disabled={!newRule.trim()}
          >
            <IconPlus size={16} />
            Adicionar
          </button>
        </div>

        {config.rules.length > 0 ? (
          <div className="bh-rules">
            {config.rules.map((rule, index) => (
              <div key={index} className="bh-rule">
                <div className="bh-rule__content">
                  <span className="bh-rule__number">{index + 1}.</span>
                  <span className="bh-rule__text">{rule}</span>
                </div>
                <button
                  type="button"
                  className="bh-rule__btn"
                  onClick={() => removeRule(index)}
                >
                  <IconX size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="bh-empty">
            <span className="bh-empty__text">
              Nenhuma regra adicionada. Adicione regras para orientar o comportamento do agente.
            </span>
          </div>
        )}

        <div className="bh-suggestions" style={{ marginTop: '0.75rem' }}>
          <span className="bh-suggestions__label">Sugestões de regras:</span>
          <div className="bh-suggestions__chips">
            {RULE_SUGGESTIONS.map((suggestion, i) => {
              const alreadyAdded = config.rules.includes(suggestion);
              return (
                <button
                  key={i}
                  type="button"
                  className={`bh-chip ${alreadyAdded ? 'bh-chip--added' : ''}`}
                  onClick={() => {
                    if (!alreadyAdded) {
                      handleChange('rules', [...config.rules, suggestion]);
                    }
                  }}
                >
                  {suggestion}
                  {!alreadyAdded && <IconPlus size={10} />}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== Section 2: Atendimento Humano ====== */}
      <section className="bh-section">
        <h3 className="bh-section__title">
          <IconUserPlus size={20} />
          Atendimento Humano
        </h3>
        <p className="bh-section__subtitle">
          Configure quando e como transferir o atendimento para uma pessoa.
        </p>

        <div className="bh-human">

          {/* 2a. Condições de Transferência */}
          <div className="bh-human__group">
            <span className="bh-human__group-title">
              <IconAlertTriangle size={14} />
              Condições de Transferência
            </span>
            <span className="bh-human__group-hint">
              Selecione em quais situações o agente deve transferir o atendimento para um humano
            </span>
            <div className="bh-conditions">
              {TRANSFER_CONDITIONS.map((condition) => {
                const isActive = config.transfer_conditions.includes(condition.id);
                return (
                  <div key={condition.id}>
                    <div
                      className={`bh-condition ${isActive ? 'bh-condition--active' : ''}`}
                      onClick={() => toggleCondition(condition.id)}
                    >
                      <div className="bh-condition__check">
                        {isActive && <IconCheck size={12} />}
                      </div>
                      <div className="bh-condition__info">
                        <span className="bh-condition__label">{condition.name}</span>
                        <span className="bh-condition__desc">{condition.description}</span>
                      </div>
                    </div>
                    {/*
                      DESATIVADO — Input de "max_attempts_before_transfer".
                      O item 'max_attempts' foi removido de TRANSFER_CONDITIONS
                      (constants/index.js) porque o backend não tem contador
                      determinístico — apenas injeta o valor como instrução
                      textual no system prompt do LLM. Para reativar, é
                      necessário implementar contador real em Redis/state.
                      Mantendo o bloco aqui (comentado) para retomada futura.

                      {condition.id === 'max_attempts' && isActive && (
                        <div className="bh-condition__extra">
                          <span className="bh-human__group-hint">
                            Após quantas tentativas sem sucesso o agente deve transferir
                          </span>
                          <input
                            type="number"
                            className="bh-input bh-input--small"
                            min={1}
                            max={10}
                            value={config.max_attempts_before_transfer}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleChange('max_attempts_before_transfer', parseInt(e.target.value) || 3)}
                          />
                        </div>
                      )}
                    */}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 2b. Regras personalizadas de transferência */}
          <div className="bh-human__group">
            <hr className="bh-human__divider" />
            <span className="bh-human__group-title">
              Regras personalizadas de transferência
            </span>
            <span className="bh-human__group-hint">
              Adicione situações específicas do seu negócio em que o agente deve transferir para humano
            </span>

            <div className="bh-rule-input">
              <input
                type="text"
                className="bh-rule-input__field"
                placeholder="Ex: Antes de realizar um agendamento"
                value={newTransferRule}
                onChange={(e) => setNewTransferRule(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTransferRule(); } }}
              />
              <button
                type="button"
                className="bh-rule-input__btn"
                onClick={addTransferRule}
                disabled={!newTransferRule.trim()}
              >
                <IconPlus size={16} />
              </button>
            </div>

            {(config.custom_transfer_rules || []).length > 0 && (
              <div className="bh-rules">
                {config.custom_transfer_rules.map((rule, index) => (
                  <div key={index} className="bh-rule">
                    <span className="bh-rule__text">{rule}</span>
                    <button
                      type="button"
                      className="bh-rule__btn"
                      onClick={() => removeTransferRule(index)}
                    >
                      <IconX size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="bh-suggestions__chips">
              {TRANSFER_SUGGESTIONS.filter(s => !(config.custom_transfer_rules || []).includes(s)).map((suggestion, i) => (
                <button
                  key={i}
                  type="button"
                  className="bh-chip"
                  onClick={() => handleChange('custom_transfer_rules', [...(config.custom_transfer_rules || []), suggestion])}
                >
                  {suggestion}
                  <IconPlus size={10} />
                </button>
              ))}
            </div>
          </div>

          {/* 2c. Departamento */}
          <div className="bh-human__group">
            <hr className="bh-human__divider" />
            <span className="bh-human__group-title">
              <IconBuilding size={14} />
              Departamento
            </span>
            <span className="bh-human__group-hint">
              Para qual departamento direcionar as transferências
            </span>
            <DepartmentSelect
              value={config.department_id}
              onChange={(value) => handleChange('department_id', value)}
            />
          </div>

          {/* 2d. Canais de Notificação */}
          <div className="bh-human__group">
            <hr className="bh-human__divider" />
            <span className="bh-human__group-title">
              <IconBell size={14} />
              Canais de Notificação
            </span>
            <span className="bh-human__group-hint">
              Como você deseja ser notificado quando um cliente solicitar atendimento humano
            </span>

            <div className="bh-channels">
              <button
                type="button"
                className={`bh-channel ${(config.notification_channels || []).includes('plataforma') ? 'bh-channel--active' : ''}`}
                onClick={() => toggleChannel('plataforma')}
              >
                {(config.notification_channels || []).includes('plataforma') && <IconCheck size={14} />}
                Plataforma (popup)
              </button>
              <button
                type="button"
                className={`bh-channel ${whatsappEnabled ? 'bh-channel--active' : ''}`}
                onClick={() => toggleChannel('whatsapp')}
              >
                {whatsappEnabled && <IconCheck size={14} />}
                <IconBrandWhatsapp size={14} />
                WhatsApp
              </button>
            </div>

            {/* WhatsApp Config */}
            {whatsappEnabled && (
              <div className="bh-whatsapp">
                <span className="bh-whatsapp__title">
                  <IconBrandWhatsapp size={16} />
                  Configuração WhatsApp
                </span>

                <PhoneList
                  phones={config.whatsapp_phones}
                  onChange={(phones) => handleChange('whatsapp_phones', phones)}
                  max={3}
                />

                <div>
                  <span className="bh-whatsapp__label">Template da mensagem</span>
                  <span className="bh-whatsapp__hint">
                    Variáveis: {'{client_phone}'}, {'{client_name}'}, {'{datetime}'}, {'{context_summary}'}, {'{reason}'}, {'{department}'}
                  </span>
                  <textarea
                    className="bh-textarea"
                    rows={6}
                    maxLength={4096}
                    value={config.whatsapp_template}
                    onChange={(e) => handleChange('whatsapp_template', e.target.value)}
                  />
                  <small style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #5a6a85)' }}>
                    {(config.whatsapp_template || '').length} / 4096 caracteres
                    {(config.whatsapp_template || '').length > 3800 && (
                      <span style={{ color: '#dc3545', marginLeft: 8 }}>
                        ⚠ Próximo do limite WhatsApp (4096). Após substituir variáveis,
                        a mensagem será truncada se exceder.
                      </span>
                    )}
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* 2e. Horário de Atendimento Humano */}
          <div className="bh-human__group">
            <hr className="bh-human__divider" />
            <span className="bh-human__group-title">
              <IconCalendar size={14} />
              Horário de Atendimento Humano
            </span>
            <span className="bh-human__group-hint">
              Defina os horários em que a equipe está disponível para atender
            </span>

            <div className="bh-schedule">
              <div className="bh-schedule__toggle">
                <Form.Check
                  type="switch"
                  id="enforce-working-hours"
                  label="Restringir transferência por horário"
                  checked={config.enforce_working_hours}
                  onChange={(e) => handleChange('enforce_working_hours', e.target.checked)}
                />
              </div>
              <span className="bh-schedule__hint">
                Se ativo, fora do horário o agente informará que não há atendentes e continuará o atendimento.
              </span>

              {config.enforce_working_hours && (
                <>
                  <div className="bh-schedule__row">
                    <span className="bh-schedule__label">De</span>
                    <select
                      className="bh-select bh-input--time"
                      value={config.working_hours_start}
                      onChange={(e) => handleChange('working_hours_start', e.target.value)}
                    >
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <span className="bh-schedule__label">Até</span>
                    <select
                      className="bh-select bh-input--time"
                      value={config.working_hours_end}
                      onChange={(e) => handleChange('working_hours_end', e.target.value)}
                    >
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>

                  <div className="bh-schedule__days">
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        className={`bh-schedule__day ${
                          (config.working_days || []).includes(day.value) ? 'bh-schedule__day--active' : ''
                        }`}
                        onClick={() => toggleDay(day.value)}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>

                  <div className="bh-out-hours">
                    <span className="bh-out-hours__label">Ação fora do horário</span>
                    <select
                      className="bh-select"
                      value={config.out_of_hours_action}
                      onChange={(e) => handleChange('out_of_hours_action', e.target.value)}
                    >
                      <option value="continue_ai">IA continua atendendo</option>
                      <option value="message_only">Apenas informar e encerrar</option>
                    </select>
                  </div>

                  <div className="bh-out-hours">
                    <span className="bh-out-hours__label">Mensagem fora do horário</span>
                    <span className="bh-human__group-hint">
                      Variáveis: {'{hours_start}'}, {'{hours_end}'}, {'{working_days}'}
                    </span>
                    <textarea
                      className="bh-textarea"
                      rows={2}
                      value={config.out_of_hours_message}
                      onChange={(e) => handleChange('out_of_hours_message', e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* ====== Section: Desligar a IA por horário ====== */}
      <section className="bh-section">
        <h3 className="bh-section__title">
          <IconClock size={20} />
          Desligar a IA por horário
        </h3>
        <p className="bh-section__subtitle">
          Defina os horários em que a IA fica <strong>desligada</strong> e o atendimento é feito
          por humanos. Fora desses horários a IA volta a responder sozinha. Todos os horários
          são no fuso de São Paulo.
        </p>

        <div className="bh-human">
          <div className="bh-human__group">
            <div className="bh-schedule__toggle">
              <Form.Check
                type="switch"
                id="agent-schedule-enabled"
                label="Desligar a IA em horários específicos"
                checked={config.agent_schedule?.enabled || false}
                onChange={(e) => toggleScheduleEnabled(e.target.checked)}
              />
            </div>
            <span className="bh-schedule__hint">
              Dentro da janela marcada, a IA não responde às mensagens recebidas — elas aparecem
              na Central para um atendente humano. Lembretes e follow-ups automáticos continuam
              funcionando normalmente.
            </span>

            {config.agent_schedule?.enabled && (
              <div className="bh-agent-schedule">
                {WEEKDAYS.map((day) => {
                  const win = config.agent_schedule?.windows?.[day.value]
                    || { off: false, start: '08:00', end: '18:00' };
                  const invalid = win.off && win.start >= win.end;
                  return (
                    <div key={day.value} className="bh-agent-schedule__day">
                      <div className="bh-agent-schedule__day-head">
                        <span className="bh-agent-schedule__day-name">{day.label}</span>
                        <Form.Check
                          type="switch"
                          id={`schedule-off-${day.value}`}
                          label="IA desligada"
                          checked={win.off}
                          onChange={(e) => updateScheduleDay(day.value, { off: e.target.checked })}
                        />
                      </div>
                      {win.off && (
                        <>
                          <div className="bh-schedule__row">
                            <span className="bh-schedule__label">Das</span>
                            <select
                              className="bh-select bh-input--time"
                              value={win.start}
                              onChange={(e) => updateScheduleDay(day.value, { start: e.target.value })}
                            >
                              {TIME_OPTIONS.map((time) => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                            <span className="bh-schedule__label">às</span>
                            <select
                              className="bh-select bh-input--time"
                              value={win.end}
                              onChange={(e) => updateScheduleDay(day.value, { end: e.target.value })}
                            >
                              {TIME_OPTIONS.map((time) => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                          </div>
                          {invalid && (
                            <span className="bh-agent-schedule__warn">
                              O horário final deve ser maior que o inicial — este dia será ignorado.
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ====== Section: Limite de Imagens da Base de Conhecimento ====== */}
      <section className="bh-section">
        <h3 className="bh-section__title">
          <IconPhoto size={20} />
          Limite de imagens da base de conhecimento
        </h3>
        <p className="bh-section__subtitle">
          Limita quantas imagens vindas da base de conhecimento o agente pode enviar
          em UMA resposta. Útil quando os documentos contêm muitas imagens por item
          (ex: catálogo com 12 fotos do mesmo produto) e você quer controlar a
          experiência do cliente. Imagens da galeria do agente NÃO são afetadas —
          apenas URLs encontradas na base de conhecimento.
        </p>

        <div className="bh-human">
          <div className="bh-human__group">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span className="bh-human__group-title" style={{ marginBottom: 0 }}>
                Máximo de imagens por resposta
              </span>
              <input
                type="number"
                className="bh-input bh-input--small"
                min={0}
                max={15}
                value={config.max_images_per_response}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    handleChange('max_images_per_response', 0);
                    return;
                  }
                  let val = parseInt(raw, 10);
                  if (isNaN(val) || val < 0) val = 0;
                  if (val > 15) val = 15;
                  handleChange('max_images_per_response', val);
                }}
                style={{ width: '90px' }}
              />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #5a6a85)' }}>
                {config.max_images_per_response === 0
                  ? '(0 = sem limite — agente envia todas as imagens encontradas)'
                  : `(envia no máximo ${config.max_images_per_response} ${config.max_images_per_response === 1 ? 'imagem' : 'imagens'})`}
              </span>
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary, #5a6a85)' }}>
              Faixa permitida: 1 a 15. Use 0 para desativar o limite.
            </div>
          </div>
        </div>
      </section>

      {/* ====== Section 3: Reativação Automática da IA ====== */}
      <section className="bh-section">
        <h3 className="bh-section__title">
          <IconClock size={20} />
          Reativação Automática da IA
        </h3>
        <p className="bh-section__subtitle">
          Quando um operador humano assume a conversa, a IA pode ser reativada automaticamente
          após o tempo configurado. Se desabilitado, usa a configuração global da conta.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 0.75rem', marginBottom: '1rem', background: 'var(--bg-tertiary, #f0f4ff)', borderRadius: '8px', fontSize: '0.8rem', color: 'var(--text-secondary, #5a6a85)' }}>
          <IconInfoCircle size={16} style={{ flexShrink: 0, color: 'var(--primary, #4a7cff)' }} />
          <span>Este recurso funciona apenas em conversas reais. Não é ativado no Playground de testes.</span>
        </div>

        <div className="bh-human">
          <div className="bh-human__group">
            <div className="bh-schedule__toggle">
              <Form.Check
                type="switch"
                id="reactivation-enabled"
                label="Habilitar reativação automática da IA"
                checked={reactivationConfig.enabled}
                disabled={reactivationLoading}
                onChange={(e) => saveReactivationConfig({
                  ...reactivationConfig,
                  enabled: e.target.checked,
                })}
              />
            </div>

            {reactivationConfig.enabled && (
              <div style={{ marginTop: '1rem' }}>
                <span className="bh-human__group-title">
                  Tempo para reativação (minutos)
                </span>
                <span className="bh-human__group-hint">
                  Após este tempo sem novas mensagens do operador, a IA retoma o atendimento automaticamente. Mínimo: 5 min, Máximo: 1440 min (24h).
                </span>
                <input
                  type="number"
                  className="bh-input bh-input--small"
                  min={5}
                  max={1440}
                  value={reactivationConfig.auto_reactivation_minutes}
                  disabled={reactivationLoading}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 10;
                    setReactivationConfig(prev => ({
                      ...prev,
                      auto_reactivation_minutes: val,
                    }));
                  }}
                  onBlur={(e) => {
                    let val = parseInt(e.target.value) || 10;
                    if (val < 5) val = 5;
                    if (val > 1440) val = 1440;
                    saveReactivationConfig({
                      ...reactivationConfig,
                      auto_reactivation_minutes: val,
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default BehaviorConfigTab;
